import { Text } from "@radix-ui/themes";
import React, { ReactNode } from "react";
import { FieldErrors } from "react-hook-form";
import styles from "./FieldShell.module.scss";

export type ComposedName = { parent: any; prop: any };

/**
 * Extrai a mensagem de erro de um campo. Suporta nomes compostos
 * (`{parent, prop}`) e nomes aninhados do react-hook-form (`item.0.grade`).
 */
export const resolveFieldError = (
  errors: FieldErrors<any> | undefined,
  name: string,
  composedName?: ComposedName
): string | undefined => {
  const castedErrors = errors as Record<string, any> | undefined;

  if (composedName?.parent && composedName?.prop) {
    const composed = castedErrors?.[composedName.parent]?.[composedName.prop];
    if (composed?.message) return composed.message as string;
  }

  const direct = name
    .split(".")
    .reduce<any>((acc, part) => (acc == null ? acc : acc[part]), castedErrors);

  return direct?.message as string | undefined;
};

/** Identificadores derivados do nome do campo, para ligar etiqueta, erro e ajuda. */
export const fieldIds = (name: string) => ({
  fieldId: `field-${name}`,
  errorId: `field-${name}-error`,
  hintId: `field-${name}-hint`,
});

/**
 * Atributos ARIA a aplicar ao controlo. `describedBy` liga a mensagem de erro
 * (ou a ajuda) ao campo, para que os leitores de ecrã a anunciem.
 */
export const fieldAria = (name: string, error?: string, hint?: string) => {
  const { errorId, hintId } = fieldIds(name);
  const describedBy = [error ? errorId : null, hint ? hintId : null]
    .filter(Boolean)
    .join(" ");

  return {
    "aria-invalid": error ? true : undefined,
    "aria-describedby": describedBy || undefined,
  };
};

interface FieldShellProps {
  name: string;
  label: string;
  required?: boolean;
  error?: string;
  hint?: string;
  children: ReactNode;
  /**
   * `label` para controlos únicos; `div` para grupos (rádio, checkbox), onde
   * um `<label for>` não é válido e a associação é feita por `aria-labelledby`.
   */
  labelAs?: "label" | "div";
}

/** Envolve um controlo de formulário com etiqueta, ajuda e mensagem de erro. */
export const FieldShell = ({
  name,
  label,
  required,
  error,
  hint,
  children,
  labelAs = "label",
}: FieldShellProps) => {
  const { fieldId, errorId, hintId } = fieldIds(name);

  return (
    <>
      <Text
        as={labelAs}
        size="2"
        id={labelAs === "div" ? fieldId : undefined}
        htmlFor={labelAs === "label" ? fieldId : undefined}
        className={styles.label}
      >
        {label}{" "}
        {required && (
          <Text color="red" aria-hidden="true">
            *
          </Text>
        )}
      </Text>

      {children}

      {hint && !error && (
        <Text as="p" size="1" color="gray" id={hintId} className={styles.message}>
          {hint}
        </Text>
      )}

      {error && (
        <Text
          as="p"
          size="1"
          color="red"
          id={errorId}
          role="alert"
          className={styles.message}
        >
          {error}
        </Text>
      )}
    </>
  );
};

export default FieldShell;
